import { defineStore } from 'pinia'
import {
  getCompanySettings,
  updateCompanySettings,
  type CompanySettingsType
} from '@/services/company'
import { useEmployeeStore } from '@/stores/employee'
import { logger } from '@/services/logger'
import { isAxiosError } from 'axios'
import { computed, ref } from 'vue'
import { isOnboardingType } from '@/helpers/onboarding-helpers'

export enum ONBOARDING_VERSION_ENUM {
  V1 = 1
}

export enum ONBOARDING_STEP {
  add_collaborator = 'add_collaborator',
  add_domain_name = 'add_domain_name',
  upload_travel_policies = 'upload_travel_policies'
}

const STEP_ORDER: ONBOARDING_STEP[] = [
  ONBOARDING_STEP.add_domain_name,
  ONBOARDING_STEP.add_collaborator,
  ONBOARDING_STEP.upload_travel_policies
]

export interface OnboardingStepsCollectionType {
  [ONBOARDING_STEP.add_domain_name]: boolean
  [ONBOARDING_STEP.add_collaborator]: boolean
  [ONBOARDING_STEP.upload_travel_policies]: boolean
}

export interface OnboardingType {
  version: ONBOARDING_VERSION_ENUM
  steps: OnboardingStepsCollectionType
}

const defaultCompanySettings: OnboardingType = {
  version: ONBOARDING_VERSION_ENUM.V1,
  steps: {
    [ONBOARDING_STEP.add_domain_name]: false,
    [ONBOARDING_STEP.add_collaborator]: false,
    [ONBOARDING_STEP.upload_travel_policies]: false
  }
}

export const useCompanySettingsStore = defineStore('companySettings', () => {
  const isOnboardingUiOpen = ref<boolean>(true)
  const companySettings = ref<CompanySettingsType>({})

  const resolveCompanyId = async (): Promise<string> => {
    const employeeStore = useEmployeeStore()

    if (!employeeStore.employee?.companyId) {
      await employeeStore.setEmployee()
    }

    const companyId = employeeStore.employee?.companyId
    if (!companyId) {
      throw new Error('No company Id')
    }

    return companyId
  }

  const updateOnboardingData = async (onboardingData: OnboardingType): Promise<void> => {
    try {
      const companyId = await resolveCompanyId()

      const updatedPayload = {
        ...companySettings.value,
        onboarding: onboardingData
      }

      companySettings.value = updatedPayload
      await updateCompanySettings(companyId, updatedPayload)
    } catch (error) {
      logger.error(error)

      throw error
    }
  }

  const setCompanySettings = async () => {
    try {
      const companyId = await resolveCompanyId()
      const { data } = await getCompanySettings(companyId)

      companySettings.value = data
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        await updateOnboardingData(defaultCompanySettings)
        return
      }

      logger.error(error)

      throw error
    }
  }

  const onBoarding = computed<OnboardingType | undefined>(() => {
    const onboarding = companySettings.value.onboarding

    if (!isOnboardingType(onboarding)) {
      return undefined
    }

    const orderedSteps = STEP_ORDER.reduce<OnboardingStepsCollectionType>((acc, key) => {
      acc[key] = onboarding.steps[key]

      return acc
    }, {} as OnboardingStepsCollectionType)

    return {
      version: onboarding.version,
      steps: orderedSteps
    }
  })

  return {
    isOnboardingUiOpen,
    defaultCompanySettings,
    companySettings,
    onBoarding,
    setCompanySettings,
    updateOnboardingData
  }
})
